/**
 * Extended MediaTimeDisplay that persists the remaining/elapsed time preference.
 */

import { MediaTimeDisplay } from 'media-chrome';
import { formatTime } from 'media-chrome/utils/time.js';
import { LIVE_ELAPSED_ATTR, LiveUptimeClock } from '../utils/live-uptime.ts';
import { patchSettings, readSettings } from '../utils/player-prefs.ts';

const DURATION_ONLY_ATTR = 'durationonly';
const MEDIA_SEEKABLE_ATTR = 'mediaseekable';
const INITIAL_DURATION_ATTR = 'initialduration';

class BunnyTimeDisplay extends MediaTimeDisplay {
  #preferenceRestored = false;
  #liveClock = new LiveUptimeClock();
  #edgeTick: ReturnType<typeof setTimeout> | null = null;

  static override get observedAttributes() {
    return [
      ...super.observedAttributes,
      DURATION_ONLY_ATTR,
      MEDIA_SEEKABLE_ATTR,
      INITIAL_DURATION_ATTR,
      LIVE_ELAPSED_ATTR,
      'compressed',
    ];
  }

  override connectedCallback() {
    super.connectedCallback();
    this.#applyTimeDisplayMode();
    this.update();

    this.addEventListener('click', this.#handleClick);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.#scheduleEdgeTick(false);
    this.removeEventListener('click', this.#handleClick);
  }

  override attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null,
  ) {
    super.attributeChangedCallback(name, oldValue, newValue);

    if (name === LIVE_ELAPSED_ATTR) {
      this.#liveClock.setUptime(
        newValue === null || newValue === '' ? null : Number(newValue),
      );
      this.update();
      return;
    }

    if (
      (
        name === 'notoggle' ||
        name === DURATION_ONLY_ATTR ||
        name === MEDIA_SEEKABLE_ATTR ||
        name === INITIAL_DURATION_ATTR ||
        name === 'compressed'
      ) &&
      oldValue !== newValue
    ) {
      this.#applyTimeDisplayMode();
      this.update();
    }
  }

  #applyTimeDisplayMode() {
    // notoggle and durationonly mean this instance should never toggle to remaining time.
    if (
      this.hasAttribute('notoggle') || this.hasAttribute(DURATION_ONLY_ATTR)
    ) {
      this.removeAttribute('remaining');
      return;
    }

    // Restore preference from localStorage once on first connect.
    if (!this.#preferenceRestored) {
      this.#preferenceRestored = true;
      this.#restorePreference();
    }
  }

  #restorePreference() {
    const stored = readSettings(this).timeRemaining;
    if (stored === true) {
      this.setAttribute('remaining', '');
    } else if (stored === false) {
      this.removeAttribute('remaining');
    }
    // If undefined, use default behavior (no preference stored yet)
  }

  #getMedia(): { seekable?: TimeRanges; paused?: boolean } | undefined {
    return (this.closest('media-controller') as
      | (Element & { media?: { seekable?: TimeRanges; paused?: boolean } })
      | null)?.media;
  }

  #getSeekableEnd(): number | undefined {
    // The media element's own range, when there is one: the `mediaseekable`
    // attribute is only as fresh as the last event Media Chrome heard, and in
    // Safari that is the first one - it stays put while the live edge moves on.
    const seekable = (this.#getMedia()?.seekable ?? this.mediaSeekable) as
      | [number, number]
      | TimeRanges
      | undefined;
    if (Array.isArray(seekable)) {
      const seekableEnd = seekable[1];
      return Number.isFinite(seekableEnd) ? seekableEnd : undefined;
    }
    if (
      seekable &&
      typeof seekable.length === 'number' &&
      seekable.length > 0 &&
      typeof seekable.end === 'function'
    ) {
      const seekableEnd = seekable.end(seekable.length - 1);
      return Number.isFinite(seekableEnd) ? seekableEnd : undefined;
    }
    return undefined;
  }

  /**
   * The playhead and the end of the seekable range as stream uptime, while this
   * is a live stream the server has told us the uptime of; null otherwise, and
   * the media's own timeline is shown.
   *
   * With no playhead position worth mapping, the live edge is shown, which is
   * where playback will start: there is no seekable range to measure against
   * (a timeline that has just restarted), or playback has not started, so the
   * playhead still sits at zero (a player that waits for play loads the
   * playlist but places the playhead only when it is pressed).
   */
  #getLiveTimes(): { current: number; end: number; atEdge: boolean } | null {
    // A finite duration is not live (an ended stream's recording is a plain
    // video), whatever the server last said.
    if (!this.#liveClock.hasUptime || Number.isFinite(this.mediaDuration)) {
      return null;
    }
    const seekableEnd = this.#getSeekableEnd();
    const offset = this.#liveClock.offsetFor(seekableEnd);
    const notStarted = this.#getCurrentTime() === 0 &&
      this.#getMedia()?.paused !== false;
    if (offset === null || seekableEnd === undefined || notStarted) {
      const edge = this.#liveClock.edgeUptime() ?? 0;
      return { current: edge, end: edge, atEdge: true };
    }
    return {
      current: Math.max(0, this.#getCurrentTime() + offset),
      end: seekableEnd + offset,
      atEdge: false,
    };
  }

  /**
   * The live edge moves on its own while nothing is playing, and nothing else
   * re-renders a player that has loaded nothing, so tick it here. Playback and
   * the status poll drive the display the rest of the time.
   */
  #scheduleEdgeTick(active: boolean) {
    if (!active) {
      if (this.#edgeTick !== null) clearTimeout(this.#edgeTick);
      this.#edgeTick = null;
      return;
    }
    if (this.#edgeTick !== null) return;
    this.#edgeTick = setTimeout(() => {
      this.#edgeTick = null;
      this.update();
    }, 1000);
  }

  #getEndTime(): number {
    if (Number.isFinite(this.mediaDuration)) {
      return this.mediaDuration;
    }
    const seededDuration = Number(this.getAttribute(INITIAL_DURATION_ATTR));
    if (Number.isFinite(seededDuration) && seededDuration > 0) {
      return seededDuration;
    }
    const seekableEnd = this.#getSeekableEnd();
    if (typeof seekableEnd === 'number') {
      return seekableEnd;
    }
    return 0;
  }

  #getCurrentTime(): number {
    return Number.isFinite(this.mediaCurrentTime) ? this.mediaCurrentTime : 0;
  }

  override update() {
    const slot = this.shadowRoot?.querySelector('slot');
    if (!slot) return;

    const live = this.#getLiveTimes();
    this.#scheduleEdgeTick(live?.atEdge === true);
    const endTime = live?.end ?? this.#getEndTime();
    const currentTime = live?.current ?? this.#getCurrentTime();
    const timeLabel = this.hasAttribute('remaining')
      ? formatTime(0 - (endTime - currentTime))
      : formatTime(currentTime);
    const displayLabel = this.hasAttribute(DURATION_ONLY_ATTR)
      ? formatTime(endTime)
      : this.hasAttribute('showduration') && !this.hasAttribute('compressed')
      ? `${timeLabel} / ${formatTime(endTime)}`
      : timeLabel;

    if (slot.textContent !== displayLabel) {
      slot.textContent = displayLabel;
    }
    this.setAttribute('aria-valuetext', displayLabel);
  }

  /**
   * Save preference after Media Chrome toggles the attribute.
   *
   * A task rather than requestAnimationFrame: rAF does not fire while the player's
   * iframe is hidden or offscreen, which silently dropped the write. It has to be a
   * task rather than a microtask too - a microtask checkpoint runs between event
   * listeners, so if Media Chrome's click listener were ever registered after ours (it
   * re-registers when `notoggle` is removed) we would read the attribute before it was
   * toggled and store the inverted value.
   */
  #handleClick = () => {
    setTimeout(() => {
      if (
        this.hasAttribute('notoggle') || this.hasAttribute(DURATION_ONLY_ATTR)
      ) {
        this.removeAttribute('remaining');
        return;
      }

      patchSettings(this, { timeRemaining: this.hasAttribute('remaining') });
    });
  };
}

if (!globalThis.customElements.get('bunny-time-display')) {
  globalThis.customElements.define('bunny-time-display', BunnyTimeDisplay);
}

export { BunnyTimeDisplay };
